// Performance optimization utilities

// Debounce function for scroll events
export const debounce = <T extends (...args: any[]) => any>(
    func: T,
    delay: number
): ((...args: Parameters<T>) => void) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    };
};

// Throttle function for frequent events
export const throttle = <T extends (...args: any[]) => any>(
    func: T,
    limit: number
): ((...args: Parameters<T>) => void) => {
    let inThrottle: boolean;
    return (...args: Parameters<T>) => {
        if (!inThrottle) {
            func(...args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
};

// Intersection Observer for lazy loading
export const createIntersectionObserver = (
    callback: IntersectionObserverCallback,
    options: IntersectionObserverInit = {}
): IntersectionObserver => {
    const defaultOptions: IntersectionObserverInit = {
        root: null,
        rootMargin: '50px',
        threshold: 0.1,
        ...options,
    };

    return new IntersectionObserver(callback, defaultOptions);
};

// Preload critical images
export const preloadCriticalImages = (imageSrcs: string[]): Promise<void[]> => {
    const promises = imageSrcs.map(src => {
        return new Promise<void>((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
            img.src = src;
        });
    });

    return Promise.all(promises);
};

// Lazy load images with intersection observer
export const lazyLoadImage = (
    img: HTMLImageElement,
    src: string,
    options: IntersectionObserverInit = {}
): void => {
    const observer = createIntersectionObserver(
        (entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const target = entry.target as HTMLImageElement;
                    target.src = src;
                    target.classList.remove('lazy');
                    observer.unobserve(target);
                }
            });
        },
        options
    );

    observer.observe(img);
};

// Measure performance
export const measurePerformance = (name: string, fn: () => void): void => {
    const start = performance.now();
    fn();
    const end = performance.now();
    console.log(`${name} took ${end - start} milliseconds`);
};

// Memory usage monitoring
export const getMemoryUsage = (): any | null => {
    if ('memory' in performance) {
        return (performance as any).memory;
    }
    return null;
};

// Virtual scrolling helper
export const calculateVisibleItems = (
    containerHeight: number,
    itemHeight: number,
    scrollTop: number,
    totalItems: number,
    buffer = 5
): { startIndex: number; endIndex: number; visibleItems: number } => {
    const visibleItems = Math.ceil(containerHeight / itemHeight);
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - buffer);
    const endIndex = Math.min(totalItems - 1, startIndex + visibleItems + buffer * 2);

    return { startIndex, endIndex, visibleItems };
};

// Resource hints for preloading
export const addResourceHints = (resources: Array<{ href: string; as: string; type?: string }>): void => {
    resources.forEach(resource => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = resource.href;
        link.as = resource.as;
        if (resource.type) {
            link.type = resource.type;
        }
        document.head.appendChild(link);
    });
};

// Bundle size analyzer helper
export const logBundleSize = (componentName: string, size: number): void => {
    if (process.env.NODE_ENV === 'development') {
        console.log(`${componentName} bundle size: ${(size / 1024).toFixed(2)} KB`);
    }
}; 